import {
    BufferAttribute,
    BufferGeometry,
    Mesh,
    RawShaderMaterial,
    Vector3,
} from 'three';

import {
    CARTED_CHAIR_COLOR,
    CHAIR_FONT_SIZE,
    CHAIR_RADIUS,
    CHAIR_RENDER_ORDER,
    CHAIR_STROKE_WIDTH,
    DEFAULT_COLOR,
    DEFAULT_FONT_SIZE,
    DEFAULT_STROKE,
    FONT_COLOR_DARK,
    FONT_COLOR_LIGHT,
    LOCKED_CHAIR_COLOR,
    PROTECTED_CHAIR_COLOR,
    REQUESTED_CHAIR_COLOR,
    TEXT_RENDER_ORDER,
    TEXT_Z,
} from '../../constants/render-constants';
import colorHelper from '../colors-helper';
import objUtils from './obj-utils';
import fragmentShader from './shaders/circle_fragment.glsl';
import vertexShader from './shaders/circle_vertex.glsl';
import ThreeTextHelper from './three-helpers/three-text-helper/three-text-helper';

import {
    STATUS_AVAILABLE,
    STATUS_CARTED,
    STATUS_LOCKED,
    STATUS_PROTECTED,
    STATUS_RELEASING,
    STATUS_REQUESTING,
    STATUS_RESERVED,
} from '../../constants/status-constants';

const { addObject, getObject, removeObject } = objUtils;

const { getDarkColor, getLightColor, getThreeColor, isDark } = colorHelper;

const BIG_NUMBER = 1e20;
const SMALL_NUMBER = -1e20;

const _chairMaterial = new RawShaderMaterial({
    fragmentShader,
    transparent: true,
    uniforms: {
        strokeWidth: { value: CHAIR_STROKE_WIDTH },
        vColorAltStep: { value: 0.5 },
    },
    vertexShader,
});

const _colorAnimationPeriod = 3000;

let _animatingColors = false;

const animateColors = () => {
    const now = Date.now();
    const remainder = now % _colorAnimationPeriod;
    const step = remainder / _colorAnimationPeriod;
    const modulatedStep = (-Math.cos(step * Math.PI * 2) + 1) * 0.5;

    _chairMaterial.uniforms.vColorAltStep.value = modulatedStep;
    requestAnimationFrame(animateColors);
};

_chairMaterial.depthTest = false;

const _addLabel = ({ sChairsText, text, x, y }) => {
    const min = new Vector3();
    const max = new Vector3();

    min.set(x, y, 0);
    max.set(x, y, 0);
    sChairsText.addText({
        alignmentX: 'center',
        alignmentY: 'center',
        fontSize: DEFAULT_FONT_SIZE,
        targetBox: {
            max,
            min,
        },
        text,
        z: TEXT_Z,
    });
};

const _addProperties = ({ properties, rowLabelVisibility, sChairsText }) => {
    if (!properties) {
        return;
    }
    const text = properties.l || properties.id || '';
    let hideLeft = false;
    let hideRight = false;

    switch (rowLabelVisibility) {
        case 'hideall':
            hideRight = true;
            hideLeft = true;
            break;
        case 'left':
            hideRight = true;
            break;
        case 'right':
            hideLeft = true;
            break;
        default:
            break;
    }
    if (!hideLeft && properties.label1_X) {
        _addLabel({
            sChairsText,
            text,
            x: properties.label1_X,
            y: properties.label1_Y,
        });
    }
    if (!hideRight && properties.label2_X) {
        _addLabel({
            sChairsText,
            text,
            x: properties.label2_X,
            y: properties.label2_Y,
        });
    }
    if (properties.label_X) {
        _addLabel({
            sChairsText,
            text,
            x: properties.label_X,
            y: properties.label_Y,
        });
    }
};

const _getBoundingBoxFromBoxList = (boxList) => {
    if (!boxList || !boxList.length) {
        return null;
    }
    // We initialise min and max crossed so that
    // the initial conditions are always true
    const min = {
        x: BIG_NUMBER,
        y: BIG_NUMBER,
    };
    const max = {
        x: SMALL_NUMBER,
        y: SMALL_NUMBER,
    };

    for (let i = 0; i < boxList.length; i += 4) {
        const boxMinX = boxList[i];
        const boxMinY = boxList[i + 1];
        const boxMaxX = boxList[i + 2];
        const boxMaxY = boxList[i + 3];

        if (boxMinX < min.x) {
            min.x = boxMinX;
        }
        if (boxMinY < min.y) {
            min.y = boxMinY;
        }
        if (boxMaxX > max.x) {
            max.x = boxMaxX;
        }
        if (boxMaxY > max.y) {
            max.y = boxMaxY;
        }
    }
    return {
        max,
        min,
    };
};

// Returns buffers associated to the chairs of the object, retrieving the
// existing one or creating new buffers if something changed
const _getChairsElements = ({ chairs, chairsHash, sChairs }) => {
    let vertices;
    let uvs;
    let indices;
    let colorsArray;
    let colorsAltArray;
    let strokeArray;
    let chairBoxes;
    let chairTiers;
    let textIndices;

    const sChairsToUse = sChairs;
    const geometry = sChairsToUse.geometry;
    let totalChairCount = sChairsToUse.userData.totalChairCount;

    if (chairsHash !== sChairsToUse.userData.hash) {
        sChairsToUse.userData.hash = chairsHash;
        totalChairCount = 0;

        for (let i = 0; i < chairs.length; i++) {
            const row = chairs[i];

            if (!row) {
                continue;
            }
            totalChairCount += row.x.length;
        }

        // we have a total of 4 vertices per chair, forming a rectangle
        // Each vertex has 3 coordinates (x,y,z), therefore we have 4*3 floats to
        // allocate per chair
        vertices = new Float32Array(3 * 4 * totalChairCount);
        // We have 4 verticex per chair, and 2 texture coordinates per vertex
        // therefore we need to allocate 4*2 floats per chair
        uvs = new Float32Array(2 * 4 * totalChairCount);
        // We have 2 triangles per rectangle. Each triangle needs 3 indices to
        // the corresponding vertices. Therefore, we need 6 indices per chair
        indices = new Uint32Array(6 * totalChairCount);

        // Each color contains 4 components (r,g,b,a), therefore we need
        // 4 floats per chair
        colorsArray = new Float32Array(4 * 4 * totalChairCount);
        colorsAltArray = new Float32Array(4 * 4 * totalChairCount);

        // Each chair has a stroke, which can change color. We need 4 attributes
        // one per vertex, and rgb. The shader will use the alpha from the chair
        // color. That's why colors are 4 float elements and strokes only 3
        strokeArray = new Float32Array(3 * 4 * totalChairCount);

        // Each chair box (for picking), has 4 coordinates (xmin,ymin,xmax,ymax)
        // therefore, we need to allocate 4 floats per chair
        // Note: This array is NOT FOR WEBGL, but for three-viewer ray casting
        chairBoxes = new Float32Array(2 * 2 * totalChairCount);
        chairTiers = new Int32Array(totalChairCount);
        // For every chair, we need the start and end indices for the
        // text color, as text
        // can expand through multiple rectangles (characters)
        textIndices = new Int32Array(2 * totalChairCount);
        // We initialise the text indices to
        for (let i = 0; i < totalChairCount * 2; i++) {
            textIndices[i] = -1;
        }

        geometry.deleteAttribute('color');
        geometry.deleteAttribute('position');
        geometry.deleteAttribute('uv');
        geometry.deleteAttribute('stroke');

        geometry.setAttribute('color', new BufferAttribute(colorsArray, 4));
        geometry.setAttribute(
            'colorAlt',
            new BufferAttribute(colorsAltArray, 4),
        );
        geometry.setAttribute('position', new BufferAttribute(vertices, 3));
        geometry.setAttribute('uv', new BufferAttribute(uvs, 2));
        geometry.setAttribute('stroke', new BufferAttribute(strokeArray, 3));
        geometry.setIndex(new BufferAttribute(indices, 1));
        sChairsToUse.userData.attributes = {
            chairBoxes,
            chairTiers,
            colorsArray,
            colorsAltArray,
            indices,
            strokeArray,
            textIndices,
            uvs,
            vertices,
        };
    } else {
        const attributes = sChairsToUse.userData.attributes;

        vertices = attributes.vertices;
        uvs = attributes.uvs;
        indices = attributes.indices;
        colorsArray = attributes.colorsArray;
        colorsAltArray = attributes.colorsAltArray;
        strokeArray = attributes.strokeArray;
        chairBoxes = attributes.chairBoxes;
        chairTiers = attributes.chairTiers;
        textIndices = attributes.textIndices;
    }

    return {
        chairBoxes,
        chairTiers,
        colorsArray,
        colorsAltArray,
        indices,
        strokeArray,
        textIndices,
        totalChairCount,
        uvs,
        vertices,
    };
};

// This will recreate all labels and seats. This will only happen in the designer
// whenever something in a section changes. We assume that the time it takes to recreate
// a section is neglectable (should be way below 1 ms)

const _fillChairs = (params) => {
    const {
        chairBoxes,
        chairTiers,
        chairs,
        colorsArray,
        colorsAltArray,
        indices,
        sChairsText,
        strokeArray,
        textIndices,
        uvs,
        vertices,
    } = params;

    sChairsText.clear();
    const chairIds = [];
    // Below: fast dictionary to associate a chair Id with an index
    // in the chairs object so that we can change colors, etc.
    const chairToIndex = {};

    const min = new Vector3();
    const max = new Vector3();
    const center = new Vector3();

    const sx = +CHAIR_RADIUS;
    const sy = +CHAIR_RADIUS;

    const defaultChairColor = getThreeColor(DEFAULT_COLOR);
    const defaultStrokeColor = getThreeColor(DEFAULT_STROKE);

    let chairIndex = 0;

    for (let k = 0; k < chairs.length; k++) {
        const row = chairs[k];

        if (!row) {
            continue;
        }
        const { d = [], id = [], l = [], t = [], x = [], y = [] } = row;

        for (let j = 0; j < x.length; j++) {
            let chairAlpha = 1;
            const cx = +x[j];
            const cy = +y[j];
            const chairId = id[j];
            const deleted = d[j];
            const tier = t[j] || 0;

            chairIds.push(chairId);
            chairToIndex[chairId] = chairIndex;
            chairTiers[chairIndex] = tier;

            // We are only rendering the seats that are not deleted
            if (!deleted) {
                chairAlpha = 1;
                uvs[chairIndex * 8 + 0] = 0;
                uvs[chairIndex * 8 + 1] = 0;
                uvs[chairIndex * 8 + 2] = 1;
                uvs[chairIndex * 8 + 3] = 0;
                uvs[chairIndex * 8 + 4] = 0;
                uvs[chairIndex * 8 + 5] = 1;
                uvs[chairIndex * 8 + 6] = 1;
                uvs[chairIndex * 8 + 7] = 1;

                vertices[chairIndex * 12 + 0] = -1 * sx + cx;
                vertices[chairIndex * 12 + 1] = 1 * sy + cy;
                vertices[chairIndex * 12 + 2] = 0;
                vertices[chairIndex * 12 + 3] = 1 * sx + cx;
                vertices[chairIndex * 12 + 4] = 1 * sy + cy;
                vertices[chairIndex * 12 + 5] = 0;
                vertices[chairIndex * 12 + 6] = -1 * sx + cx;
                vertices[chairIndex * 12 + 7] = -1 * sy + cy;
                vertices[chairIndex * 12 + 8] = 0;
                vertices[chairIndex * 12 + 9] = 1 * sx + cx;
                vertices[chairIndex * 12 + 10] = -1 * sy + cy;
                vertices[chairIndex * 12 + 11] = 0;

                indices[chairIndex * 6 + 0] = chairIndex * 4 + 2;
                indices[chairIndex * 6 + 1] = chairIndex * 4 + 0;
                indices[chairIndex * 6 + 2] = chairIndex * 4 + 1;
                indices[chairIndex * 6 + 3] = chairIndex * 4 + 3;
                indices[chairIndex * 6 + 4] = chairIndex * 4 + 2;
                indices[chairIndex * 6 + 5] = chairIndex * 4 + 1;

                colorsArray[chairIndex * 16 + 0] = defaultChairColor.r;
                colorsArray[chairIndex * 16 + 1] = defaultChairColor.g;
                colorsArray[chairIndex * 16 + 2] = defaultChairColor.b;
                colorsArray[chairIndex * 16 + 3] = chairAlpha;
                colorsArray[chairIndex * 16 + 4] = defaultChairColor.r;
                colorsArray[chairIndex * 16 + 5] = defaultChairColor.g;
                colorsArray[chairIndex * 16 + 6] = defaultChairColor.b;
                colorsArray[chairIndex * 16 + 7] = chairAlpha;
                colorsArray[chairIndex * 16 + 8] = defaultChairColor.r;
                colorsArray[chairIndex * 16 + 9] = defaultChairColor.g;
                colorsArray[chairIndex * 16 + 10] = defaultChairColor.b;
                colorsArray[chairIndex * 16 + 11] = chairAlpha;
                colorsArray[chairIndex * 16 + 12] = defaultChairColor.r;
                colorsArray[chairIndex * 16 + 13] = defaultChairColor.g;
                colorsArray[chairIndex * 16 + 14] = defaultChairColor.b;
                colorsArray[chairIndex * 16 + 15] = chairAlpha;

                for (let i = 0; i < 16; i++) {
                    const index = chairIndex * 16 + i;

                    colorsAltArray[index] = colorsArray[index];
                }

                strokeArray[chairIndex * 12 + 0] = defaultStrokeColor.r;
                strokeArray[chairIndex * 12 + 1] = defaultStrokeColor.g;
                strokeArray[chairIndex * 12 + 2] = defaultStrokeColor.b;
                strokeArray[chairIndex * 12 + 3] = defaultStrokeColor.r;
                strokeArray[chairIndex * 12 + 4] = defaultStrokeColor.g;
                strokeArray[chairIndex * 12 + 5] = defaultStrokeColor.b;
                strokeArray[chairIndex * 12 + 6] = defaultStrokeColor.r;
                strokeArray[chairIndex * 12 + 7] = defaultStrokeColor.g;
                strokeArray[chairIndex * 12 + 8] = defaultStrokeColor.b;
                strokeArray[chairIndex * 12 + 9] = defaultStrokeColor.r;
                strokeArray[chairIndex * 12 + 10] = defaultStrokeColor.g;
                strokeArray[chairIndex * 12 + 11] = defaultStrokeColor.b;

                var startBoxIndex = 2 * 2 * chairIndex;

                center.set(cx, cy, 0);

                min.set(center.x - CHAIR_RADIUS, center.y - CHAIR_RADIUS, 0);
                max.set(center.x + CHAIR_RADIUS, center.y + CHAIR_RADIUS, 0);

                chairBoxes[startBoxIndex] = min.x;
                chairBoxes[startBoxIndex + 1] = min.y;

                chairBoxes[startBoxIndex + 2] = max.x;
                chairBoxes[startBoxIndex + 3] = max.y;

                const text = l[j] || chairId || '';

                const { endIndex, startIndex } = sChairsText.addText({
                    alignmentX: 'center',
                    alignmentY: 'center',
                    color: FONT_COLOR_DARK,
                    fontSize: CHAIR_FONT_SIZE,
                    targetBox: {
                        max,
                        min,
                    },
                    text,
                    z: TEXT_Z,
                });

                textIndices[chairIndex * 2 + 0] = startIndex;
                textIndices[chairIndex * 2 + 1] = endIndex;
            }
            chairIndex++;
        }
    }
    return {
        chairIds,
        chairToIndex,
    };
};

const syncChairs = ({
    chairs,
    chairsHash,
    font,
    headers,
    id,
    rowLabelVisibility,
    sParent,
    updateEvent,
}) => {
    let sChairs = getObject(sParent, 'chairs');
    let geometry;
    let sChairsText;

    if (!sChairs) {
        geometry = new BufferGeometry();

        sChairs = new Mesh(geometry, _chairMaterial);
        sChairs.visible = true;
        sChairs.renderOrder = CHAIR_RENDER_ORDER;
        sChairs.userData.weak = true;
        sChairs.userData.id = id;

        sChairsText = new ThreeTextHelper({ font });

        sChairsText.renderOrder = TEXT_RENDER_ORDER;
        addObject(sChairs, 'labels', sChairsText);
        sChairsText.name = 'labels';
        sChairsText.userData.weak = true;

        addObject(sParent, 'chairs', sChairs);
    }

    geometry = sChairs.geometry;
    sChairsText = getObject(sChairs, 'labels');
    sChairsText.clear();

    if (chairs && chairs.length) {
        const {
            chairBoxes,
            chairTiers,
            colorsArray,
            colorsAltArray,
            indices,
            strokeArray,
            textIndices,
            uvs,
            vertices,
        } = _getChairsElements({ chairs, chairsHash, sChairs });

        const { chairIds, chairToIndex } = _fillChairs({
            chairBoxes,
            chairTiers,
            chairs,
            colorsArray,
            colorsAltArray,
            indices,
            sChairsText,
            strokeArray,
            textIndices,
            uvs,
            vertices,
        });

        if (headers) {
            for (let i = 0; i < headers.length; i++) {
                const properties = headers[i];

                _addProperties({
                    properties,
                    rowLabelVisibility,
                    sChairsText,
                });
            }
        }

        sChairsText.updateGeometry();

        sChairs.userData.type = 'chairs';
        sChairs.userData.boxList = chairBoxes;
        sChairs.userData.boundingBox = _getBoundingBoxFromBoxList(chairBoxes);
        sChairs.userData.weak = true;
        sChairs.userData.referrer = {
            idToIndex: chairToIndex,
            ids: chairIds,
            type: 'chairs',
        };
    } else {
        removeObject(sParent, 'chairs', sChairs);
    }

    sChairs.userData.updateEvent = updateEvent;
};

const _getLockedFromSeatStatus = (seatStatus, usingHold, manageMode) => {
    switch (seatStatus) {
        case STATUS_LOCKED:
            return true;
        case STATUS_RESERVED:
            return !manageMode;
        case STATUS_PROTECTED:
            return false;
        case STATUS_AVAILABLE:
            if (usingHold) {
                return true;
            }
            return false;
        default:
            return false;
    }
};

const _fontColorCache = {};
const _fontColorLocked = getThreeColor(LOCKED_CHAIR_COLOR);
const _getFontColor = (chairColor, locked, lightFont, darkFont) => {
    if (locked) {
        return _fontColorLocked;
    }
    let fontColor = _fontColorCache[chairColor];

    if (fontColor) {
        return fontColor;
    }
    if (isDark(chairColor)) {
        fontColor = lightFont;
    } else {
        fontColor = darkFont;
    }
    _fontColorCache[chairColor] = fontColor;
    return fontColor;
};

const _getChairColor = ({ seatStatus, locked, baseColor, manageMode }) => {
    let chairColor;

    if (locked) {
        return LOCKED_CHAIR_COLOR;
    }
    switch (seatStatus) {
        case STATUS_CARTED:
            chairColor = CARTED_CHAIR_COLOR;
            break;
        case STATUS_RELEASING:
        case STATUS_REQUESTING:
            chairColor = REQUESTED_CHAIR_COLOR;
            break;
        case STATUS_RESERVED:
            chairColor = REQUESTED_CHAIR_COLOR;
            break;
        case STATUS_PROTECTED:
            if (manageMode) {
                chairColor = PROTECTED_CHAIR_COLOR;
            } else {
                chairColor = baseColor;
            }
            break;
        default:
            chairColor = baseColor;
    }
    return chairColor;
};

const _getChairColorAlt = ({ seatStatus, locked, baseColor, manageMode }) =>
    _getChairColor({ seatStatus, locked, baseColor, manageMode });

const _getStrokeColor = (hoverChair, locked, baseColor) => {
    if (locked) {
        return LOCKED_CHAIR_COLOR;
    }
    if (hoverChair) {
        return getLightColor(baseColor);
    }
    return getDarkColor(baseColor);
};
// Main loop. Extracted here for perfomance reasons
// as all needed content is constant and in the same scope
const _loopChairColors = (params) => {
    const {
        chairTiers,
        colorsArray,
        colorsAltArray,
        darkFont,
        hoverId,
        ids,
        labelColorsArray,
        lightFont,
        seatStatus,
        selected,
        strokeArray,
        textIndices,
        tiers,
        manageMode,
    } = params;

    const { statusCache, usingHold } = seatStatus;

    for (let chairIndex = 0; chairIndex < ids.length; chairIndex++) {
        const chairId = ids[chairIndex];
        let locked = false;
        let baseColor = DEFAULT_COLOR;
        let singleSeatStatus = undefined;
        const tierNumber = chairTiers[chairIndex];

        if (statusCache[chairId]) {
            singleSeatStatus = statusCache[chairId].s;
            const tier = tiers[tierNumber];

            locked = _getLockedFromSeatStatus(
                singleSeatStatus,
                usingHold,
                manageMode,
            );

            if (tier) {
                if (tier.color) {
                    baseColor = tier.color;
                }
                if (!tier.variants || !tier.variants.length) {
                    locked = true;
                }
            }
        } else {
            locked = true;
        }

        const hoverChair = hoverId === chairId;

        let chairColor;
        let chairColorAlt;

        if (selected.seats[chairId]) {
            chairColor = REQUESTED_CHAIR_COLOR;
            chairColorAlt = CARTED_CHAIR_COLOR;
        } else {
            chairColor = _getChairColor({
                seatStatus: singleSeatStatus,
                locked,
                baseColor,
                manageMode,
            });
            chairColorAlt = _getChairColorAlt({
                seatStatus: singleSeatStatus,
                locked,
                baseColor,
                manageMode,
            });
            if (!_animatingColors) {
                _animatingColors = true;
                animateColors();
            }
        }

        const strokeColor = _getStrokeColor(hoverChair, locked, baseColor);

        const chairColorThree = getThreeColor(chairColor);
        const strokeColorThree = getThreeColor(strokeColor);
        const chairColorAltThree = getThreeColor(chairColorAlt);

        colorsArray[chairIndex * 16 + 0] = chairColorThree.r;
        colorsArray[chairIndex * 16 + 1] = chairColorThree.g;
        colorsArray[chairIndex * 16 + 2] = chairColorThree.b;

        colorsArray[chairIndex * 16 + 4] = chairColorThree.r;
        colorsArray[chairIndex * 16 + 5] = chairColorThree.g;
        colorsArray[chairIndex * 16 + 6] = chairColorThree.b;

        colorsArray[chairIndex * 16 + 8] = chairColorThree.r;
        colorsArray[chairIndex * 16 + 9] = chairColorThree.g;
        colorsArray[chairIndex * 16 + 10] = chairColorThree.b;

        colorsArray[chairIndex * 16 + 12] = chairColorThree.r;
        colorsArray[chairIndex * 16 + 13] = chairColorThree.g;
        colorsArray[chairIndex * 16 + 14] = chairColorThree.b;

        colorsAltArray[chairIndex * 16 + 0] = chairColorAltThree.r;
        colorsAltArray[chairIndex * 16 + 1] = chairColorAltThree.g;
        colorsAltArray[chairIndex * 16 + 2] = chairColorAltThree.b;

        colorsAltArray[chairIndex * 16 + 4] = chairColorAltThree.r;
        colorsAltArray[chairIndex * 16 + 5] = chairColorAltThree.g;
        colorsAltArray[chairIndex * 16 + 6] = chairColorAltThree.b;

        colorsAltArray[chairIndex * 16 + 8] = chairColorAltThree.r;
        colorsAltArray[chairIndex * 16 + 9] = chairColorAltThree.g;
        colorsAltArray[chairIndex * 16 + 10] = chairColorAltThree.b;

        colorsAltArray[chairIndex * 16 + 12] = chairColorAltThree.r;
        colorsAltArray[chairIndex * 16 + 13] = chairColorAltThree.g;
        colorsAltArray[chairIndex * 16 + 14] = chairColorAltThree.b;

        strokeArray[chairIndex * 12 + 0] = strokeColorThree.r;
        strokeArray[chairIndex * 12 + 1] = strokeColorThree.g;
        strokeArray[chairIndex * 12 + 2] = strokeColorThree.b;
        strokeArray[chairIndex * 12 + 3] = strokeColorThree.r;
        strokeArray[chairIndex * 12 + 4] = strokeColorThree.g;
        strokeArray[chairIndex * 12 + 5] = strokeColorThree.b;
        strokeArray[chairIndex * 12 + 6] = strokeColorThree.r;
        strokeArray[chairIndex * 12 + 7] = strokeColorThree.g;
        strokeArray[chairIndex * 12 + 8] = strokeColorThree.b;
        strokeArray[chairIndex * 12 + 9] = strokeColorThree.r;
        strokeArray[chairIndex * 12 + 10] = strokeColorThree.g;
        strokeArray[chairIndex * 12 + 11] = strokeColorThree.b;

        const startIndex = 0 | textIndices[chairIndex * 2 + 0];
        const endIndex = 0 | textIndices[chairIndex * 2 + 1];

        // This continue is for performance reasons. If we create a scope
        // it makes this function extremely slow because of scoped variable access
        if (startIndex < 0) {
            continue;
        }

        const fontColor = _getFontColor(
            chairColor,
            locked,
            lightFont,
            darkFont,
        );

        for (let tIndex = startIndex; tIndex <= endIndex; tIndex++) {
            const colorIndex = (tIndex * 4) | 0;

            labelColorsArray[colorIndex + 0] = fontColor.r;
            labelColorsArray[colorIndex + 1] = fontColor.g;
            labelColorsArray[colorIndex + 2] = fontColor.b;
        }
    }
};

// This function will use tiers (tier colors), globals (mode, hover, etc),
// and objectStatus (available, hold, etc) to decide on chair color and stroke
// This way we do not have to rebuild the whole three.js object of the section every time
// there is a color change.
const syncChairColors = ({
    globalsNode,
    sParent,
    seatStatus,
    selected,
    tiers,
    manageMode,
}) => {
    if (!sParent) {
        return;
    }
    const sChairs = getObject(sParent, 'chairs');

    if (!sChairs || !globalsNode) {
        return;
    }
    const sLabels = getObject(sChairs, 'labels');

    if (!sLabels) {
        return;
    }
    const hoverId = globalsNode.hoverId || {};
    const userData = sChairs.userData;
    const lightFont = getThreeColor(FONT_COLOR_LIGHT);
    const darkFont = getThreeColor(FONT_COLOR_DARK);
    const { statusUpdateCounter } = seatStatus;

    if (
        userData.colorHash !== globalsNode ||
        userData.statusUpdateCounter !== statusUpdateCounter
    ) {
        userData.colorHash = globalsNode;
        userData.statusUpdateCounter = statusUpdateCounter;

        const geometry = sChairs.geometry;
        const textIndices = sChairs.userData.attributes.textIndices;
        const chairTiers = sChairs.userData.attributes.chairTiers;
        const colorAttribute = geometry.getAttribute('color');
        const colorAltAttribute = geometry.getAttribute('colorAlt');
        const strokeAttribute = geometry.getAttribute('stroke');
        const labelColorAttribute = sLabels.geometry.getAttribute('color');
        const colorsArray = colorAttribute.array;
        const colorsAltArray = colorAltAttribute.array;
        const labelColorsArray = labelColorAttribute.array;
        const strokeArray = strokeAttribute.array;
        const ids = userData.referrer.ids || [];

        colorAttribute.needsUpdate = true;
        colorAltAttribute.needsUpdate = true;
        strokeAttribute.needsUpdate = true;
        labelColorAttribute.needsUpdate = true;

        _loopChairColors({
            chairTiers,
            colorsArray,
            colorsAltArray,
            darkFont,
            hoverId,
            ids,
            labelColorsArray,
            lightFont,
            seatStatus,
            selected,
            strokeArray,
            textIndices,
            tiers,
            manageMode,
        });
    }
};

export default {
    syncChairColors,
    syncChairs,
};
